import type { RuleStepStateHistoryResult } from "@redotech/effect-api-shared/models/shopper-ai/rule-engine-results";
import type { AutomationExecutionResponse } from "@redotech/effect-api-shared/rpc/automation-execution";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import type { AdvancedFlow } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import type { StateHistoryEntry } from "@redotech/redo-model/advanced-flow/automation-execution-definition";
import {
  schemas,
  SchemaType,
} from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { StepType } from "@redotech/redo-model/advanced-flow/steps";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Html5DndProviderSingleton } from "@redotech/redo-web/dnd/html5-dnd-provider-singleton";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { memo, useCallback, useMemo, useState } from "react";
import { Flowchart } from "../../../../../setting/flow-editor/advanced-flow-editor/flow";
import type { FlowInProgress } from "../../../../../setting/flow-editor/advanced-flow-editor/flow-editor";
import type { PartialFlowStep } from "../../../../../setting/flow-editor/advanced-flow-editor/steps";
import { useAutomationExecution } from "../../../../hooks/use-automation-execution";
import { StepDetailsPanel } from "./step-details-panel";

export const ExecutionTreeView = memo(function ExecutionTreeView({
  workflowId,
  onClose,
}: {
  workflowId: string;
  onClose: () => void;
}) {
  const {
    data: execution,
    isLoading,
    error,
  } = useAutomationExecution(workflowId);

  if (isLoading) {
    return (
      <Flex align="center" dir="column" flex={1} justify="center" p="xl">
        <Text textColor="tertiary">Loading execution...</Text>
      </Flex>
    );
  }

  if (error || !execution) {
    return (
      <Flex align="center" dir="column" flex={1} justify="center" p="xl">
        <Text textColor="tertiary">
          {error instanceof Error ? error.message : "No execution data found"}
        </Text>
      </Flex>
    );
  }

  return <ExecutionFlowView execution={execution} onClose={onClose} />;
});

const ExecutionFlowView = memo(function ExecutionFlowView({
  execution,
  onClose,
}: {
  execution: AutomationExecutionResponse;
  onClose: () => void;
}) {
  const [selectedStepId, setSelectedStepId] = useState<string | undefined>(
    undefined,
  );

  const historyMap = useMemo(() => {
    const map = new Map<string, StateHistoryEntry>();
    for (const entry of execution.stateHistory) {
      map.set(entry.stepId, entry);
    }
    return map;
  }, [execution.stateHistory]);

  const automation: FlowInProgress = useMemo(() => {
    const flow: AdvancedFlow = execution.flow;
    // Deep clone steps so we don't mutate the cached flow object
    const steps = flow.steps.map((s) => ({ ...s })) as PartialFlowStep[];

    // Add synthetic end nodes for condition branches that don't have a target.
    // Use Extract<> to pull the matching union members from PartialFlowStep so
    // TypeScript knows nextTrueId/nextFalseId are available on these step types.
    type PartialConditionStep = Extract<
      PartialFlowStep,
      | { type: typeof StepType.RULE_CONDITIONS }
      | { type: typeof StepType.RULE_CONDITIONS_V3 }
    >;
    const conditionSteps = steps.filter(
      (s): s is PartialConditionStep =>
        s.type === StepType.RULE_CONDITIONS_V3 ||
        s.type === StepType.RULE_CONDITIONS,
    );
    for (const condStep of conditionSteps) {
      if (!condStep.nextTrueId) {
        const endId = `${condStep.id}_end_true`;
        steps.push({ type: StepType.DO_NOTHING, id: endId } as PartialFlowStep);
        condStep.nextTrueId = endId;
      }
      if (!condStep.nextFalseId) {
        const endId = `${condStep.id}_end_false`;
        steps.push({ type: StepType.DO_NOTHING, id: endId } as PartialFlowStep);
        condStep.nextFalseId = endId;
      }
    }

    return {
      _id: flow._id.toString(),
      name: flow.name,
      steps,
      category: flow.category ?? "Rules",
      schemaType: flow.schemaType ?? SchemaType.RULES_V3,
    };
  }, [execution.flow]);

  const schema = useMemo(
    () => schemas[automation.schemaType],
    [automation.schemaType],
  );

  const handleNodeClick = useCallback((nodeId: string | undefined) => {
    setSelectedStepId(nodeId);
  }, []);

  // Compute executed step IDs and execution path edges for styling
  const { executedStepIds, executionPathEdgeIds } = useMemo(() => {
    const executedIds = new Set<string>();
    const edgeIds = new Set<string>();

    // Helper type covering only the step properties needed to trace execution paths
    type StepWithEdgeProps = PartialFlowStep & {
      nextId?: string;
      nextTrueId?: string;
      nextFalseId?: string;
    };

    // The trigger is always executed
    const triggerStep = automation.steps.find(
      (s) => s.type === StepType.TRIGGER,
    ) as StepWithEdgeProps | undefined;
    if (triggerStep?.id) {
      executedIds.add(triggerStep.id);
      if (triggerStep.nextId) {
        edgeIds.add(`${triggerStep.id}_edge`);
      }
    }

    for (const entry of execution.stateHistory) {
      if (entry.stepId === "<END>") continue;
      executedIds.add(entry.stepId);

      // Find the step to determine which edge was taken to get to the next step
      const step = automation.steps.find((s) => s.id === entry.stepId) as
        | StepWithEdgeProps
        | undefined;
      if (!step?.id) continue;

      const result = entry.stateHistoryResult as
        | RuleStepStateHistoryResult
        | undefined;
      if (
        step.type === StepType.RULE_CONDITIONS_V3 ||
        step.type === StepType.RULE_CONDITIONS
      ) {
        // For conditions, highlight the branch that was taken and mark the target as executed
        const passed = result && "passed" in result ? result.passed : undefined;
        if (passed === true && step.nextTrueId) {
          edgeIds.add(`${step.id}_true`);
          executedIds.add(step.nextTrueId);
        } else if (passed === false && step.nextFalseId) {
          edgeIds.add(`${step.id}_false`);
          executedIds.add(step.nextFalseId);
        }
      } else if (step.nextId) {
        // Linear edges use the format: stepId_edge
        edgeIds.add(`${step.id}_edge`);
        executedIds.add(step.nextId);
      }
    }

    return { executedStepIds: executedIds, executionPathEdgeIds: edgeIds };
  }, [execution.stateHistory, automation.steps]);

  // Generate dynamic CSS for execution highlighting
  const executionStyles = useMemo(() => {
    const highlightColor = "var(--redo-colors-orange-dark-500)";

    const executedNodeSelectors = [...executedStepIds]
      .map((id) => `.react-flow__node[data-id="${id}"] > div`)
      .join(",\n");

    const edgeHighlightSelectors = [...executionPathEdgeIds]
      .map((id) => `.react-flow__edge[data-id="${id}"] path`)
      .join(",\n");

    const selectedNodeSelector = selectedStepId
      ? `.react-flow__node[data-id="${selectedStepId}"] > div { outline-color: var(--redo-colors-orange-dark-700) !important; }`
      : "";

    return `
      ${executedNodeSelectors ? `${executedNodeSelectors} { outline: 2px solid ${highlightColor}; border-radius: 6px; }` : ""}
      ${edgeHighlightSelectors ? `${edgeHighlightSelectors} { stroke: ${highlightColor} !important; stroke-width: 2px !important; }` : ""}
      ${selectedNodeSelector}
      .react-flow__node[data-id*="_next"] { display: none !important; }
      .react-flow__edge[data-id*="_next_edge"] { display: none !important; }
      .react-flow__edge[data-id*="_true_edge"] { display: none !important; }
      .react-flow__edge[data-id*="_false_edge"] { display: none !important; }
      .react-flow__edgelabel-renderer > div:has([data-testid="drop-zone-icon"]) { display: none !important; }
    `;
  }, [executedStepIds, executionPathEdgeIds, selectedStepId]);

  const selectedEntry = selectedStepId
    ? historyMap.get(selectedStepId)
    : undefined;
  const selectedResult = selectedEntry?.stateHistoryResult as
    | RuleStepStateHistoryResult
    | undefined;

  return (
    <Flex dir="column" flex={1} gap="none" height="full" overflow="hidden">
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        gap="sm"
        p="md"
        shrink={0}
      >
        <RedoButton
          hierarchy="tertiary"
          IconLeading={ArrowLeftIcon}
          onClick={onClose}
          size="xs"
          text="Back"
        />
        <Text fontSize="sm" fontWeight="medium">
          Execution Details
        </Text>
        {execution.flow.name && (
          <Text fontSize="sm" textColor="tertiary">
            — {execution.flow.name}
          </Text>
        )}
      </Flex>
      <Flex flex={1} overflow="hidden">
        <style>{executionStyles}</style>
        <Html5DndProviderSingleton>
          <ReactFlowProvider>
            <Flowchart
              automation={automation}
              onNodeClick={handleNodeClick}
              readonly
              schema={schema}
              selected={selectedStepId}
            />
          </ReactFlowProvider>
        </Html5DndProviderSingleton>
        {selectedStepId && (
          <StepDetailsPanel
            automation={automation}
            executed={executedStepIds.has(selectedStepId)}
            selectedResult={selectedResult}
            selectedStepId={selectedStepId}
            triggerMetadata={execution.schemaInstance?.triggerMetadata}
          />
        )}
      </Flex>
    </Flex>
  );
});
